// src/context/AuthContext.js
import React, { createContext, useContext, useEffect, useState } from "react";
import { auth, db } from "../lib/firebase";
import {
  onAuthStateChanged,
  signOut,
  createUserWithEmailAndPassword,
} from "firebase/auth";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { logError } from "../utils/logError";

const AuthContext = createContext();
export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const cached = localStorage.getItem("user");
    if (cached) {
      try {
        setCurrentUser(JSON.parse(cached));
      } catch (e) {
        console.error("Error parsing cached user:", e);
      }
    }
  }, []);

  useEffect(() => {
    console.log("Auth state changed:", currentUser);
  }, [currentUser]);

  const signup = async (email, password, managerName) => {
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      // Create Firestore user doc immediately with managerName as displayName
      await setDoc(doc(db, "users", user.uid), {
        email: user.email,
        displayName: managerName,
        leagueIDs: [],
        teams: {},
        currentLeagueID: null,
      });
      return userCredential;
    } catch (err) {
      console.error("Signup error:", err);
      await logError({
        leagueID: null,
        userID: "newUser",
        action: "AuthContext_signup",
        payload: { email },
        error: err
      });
      throw err;
    }
  };

  const logout = () => {
    return signOut(auth);
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          const userRef = doc(db, "users", user.uid);
          const userSnap = await getDoc(userRef);

          // Always fetch Firestore user document
          let firestoreData = {};
          if (userSnap.exists()) {
            firestoreData = userSnap.data();
          } else {
            // If no doc exists, create one with default values
            await setDoc(userRef, {
              email: user.email,
              displayName: user.email,
              leagueIDs: [],
              teams: {},
              currentLeagueID: null,
            });
            const newUserSnap = await getDoc(userRef);
            firestoreData = newUserSnap.exists() ? newUserSnap.data() : {};
          }

          const combinedUser = {
            uid: user.uid,
            email: user.email,
            ...firestoreData,
            displayName: firestoreData.displayName || user.displayName || user.email,
          };

          console.log("Combined user uid:", combinedUser.uid);

          setCurrentUser(combinedUser);

          // 🔑 keep localStorage always up to date
          localStorage.setItem("user", JSON.stringify(combinedUser));
        } catch (err) {
          console.error("Error fetching user doc:", err);
          await logError({
            leagueID: null,
            userID: user?.uid || "unknown",
            action: "AuthContext_onAuthStateChanged",
            payload: { email: user?.email },
            error: err
          });
          setCurrentUser(null);
          localStorage.removeItem("user");
        }
      } else {
        setCurrentUser(null);
        localStorage.removeItem("user");
      }

      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const value = { currentUser, signup, logout, loading };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};